import { ErpWorkflowOperationStep } from './../model/erpworkflowoperationstep';
import { ErpWorkflowOperation } from './../model/erpworkflowoperation';
import { HisUiService, HisNotificationService } from 'hisng';
import { ErpWorkflowService } from '../service/erpworkflow.service';
import { ErpWorkflowProcessingStepValue } from './../model/erpworkflow-values';
import { Component, OnInit, Input, OnDestroy, ViewChild } from '@angular/core';
import { ErpWorkflowPersonData } from '../model/erpworkflowpersondata';
import { Subject } from 'rxjs';
import { AutoComplete } from 'primeng/autocomplete';

@Component( {
    selector: 'erp-workflow-operation',
    templateUrl: './erpworkflowoperation.component.html'
} )
export class ErpWorkflowOperationComponent implements OnInit, OnDestroy {
    @Input() processType = '';
    @Input() useCaseIdent = '';
    @Input() processId = 0;
    @Input() field?: string;

    @Input() saveWorkflowOperationStep!: Subject<string>;
    @ViewChild('processingStepDropdown', { static: true }) processingStepDropdown: AutoComplete | undefined;
    @ViewChild('nextResponsiblePersonDropdown', { static: true }) nextResponsiblePersonDropdown: AutoComplete | undefined;

    selectedProcessingStep?: ErpWorkflowProcessingStepValue;
    selectedNextResponsiblePerson?: ErpWorkflowPersonData;
    stepExecutionComment?: string;

    // Es sollen alle Bearbeitungsschritte, die aufgrund der geladenen Bearbeitungsgruppe(n),
    // unabhängig ob deren Vorbedingungen erfüllt sind, in der Liste erscheinen.
    // Schritte, die aufgrund nicht erfüllter Vorbedingungen nicht wählbar sind, sollen erscheinen,
    // aber nicht auswählbar sein und errhalten einen Hinweis, dass diese nicht wählbar sind.
    availableProcessingStepValues: ErpWorkflowProcessingStepValue[] = [];
    filteredProcessingStepValues: ErpWorkflowProcessingStepValue[] = [];

    // Es werden zunächst alle Personen geladen, welche mindestens einen Bearbeitungsschritt durchführen können, der als
    // Vorbedingung mindestens einen der momentan wählbaren Schritte enthält. Sobald im Formular ein konkreter Schritt
    // gewählt wird, sollen nur noch Personen zur Auswahl stehen, die einen Nachfolgeschritt zum gewählten Schritt
    // ausführen können. Andere Personen sollen als nicht auswählbar dargestellt werden.
    availableNextResponsiblePersonValues: ErpWorkflowPersonData[] = [];
    filteredNextResponsiblePersonValues: ErpWorkflowPersonData[] = [];
    pseudonymizedPreallocatedPersonId = '';

    constructor(
        private readonly hisUiService: HisUiService,
        private readonly hisNotificationService: HisNotificationService,
        private readonly erpWorkflowService: ErpWorkflowService
    ) { }

    ngOnInit(): void {
        this.updateProcessingStepAndNextResponsiblePersonLists();

        this.saveWorkflowOperationStep.subscribe({ next: () => {
            this.saveWorkflowOperation();
        }});
    }

    updateProcessingStepAndNextResponsiblePersonLists() {
        this.erpWorkflowService.findNextPossibleWorkflowOperations( this.processType, this.useCaseIdent, this.processId )
            .subscribe( {
                next: ( workflowOperation: ErpWorkflowOperation ) => {
                    this.sortProcessingStepAndNextResponsiblePersonLists( workflowOperation );

                    workflowOperation.erpWorkflowProcessingStepValueDtos.forEach( processingStep => {
                        if ( !processingStep.isSelectable ) {
                            let preconditionNotFulfilled = this.hisUiService.translate( 'rm.erp.common.workflow.preconditionNotFulfilled' );
                            processingStep.text += ` (${ preconditionNotFulfilled })`;
                        }

                        this.availableProcessingStepValues.push( processingStep );
                    } );

                    workflowOperation.erpWorkflowPersonDataDtos.forEach( person => {
                        this.availableNextResponsiblePersonValues.push( person );
                    } );

                    if (workflowOperation.pseudonymizedPreallocatedPersonId) {
                        this.pseudonymizedPreallocatedPersonId = workflowOperation.pseudonymizedPreallocatedPersonId;
                        this.preselectNextResponsiblePerson(workflowOperation.erpWorkflowPersonDataDtos);
                    }
                }
            } );
    }

    ngOnDestroy() {
        this.saveWorkflowOperationStep.unsubscribe();
    }

    filterProcessingStepValues() {
        this.filteredProcessingStepValues = [];

        this.availableProcessingStepValues.forEach( processingStep => {
            this.filteredProcessingStepValues.push( processingStep );
        } );
    }

    filterNextResponsiblePersonValues() {
        this.filteredNextResponsiblePersonValues = [];

        // Es soll auch möglich sein, keine nächste zuständige Person anzugeben, da bestimmte Bearbeitungsschritte
        // einen Endzustand darstellen und es somit keine nächste zuständige Person gibt.
        this.filteredNextResponsiblePersonValues.push({});

        if (this.selectedProcessingStep) {
            this.filterNextResponsiblePeopleByFulfilledPrecondition(this.selectedProcessingStep);
        } else {
            this.availableNextResponsiblePersonValues.forEach( responsiblePerson => {
                this.filteredNextResponsiblePersonValues.push( responsiblePerson );
            } );
        }

        this.sortNextResponsiblePersonList(this.filteredNextResponsiblePersonValues);
    }

    // Workaround, da PrimeNG nur ein Attribut als "Suggestion" akzeptiert.
    // Man kann zwar im HTML per ng-template die "Suggestions" überschreiben, jedoch erscheint bei Auswahl eines Wertes
    // aus der Dropdown-Liste wiederum nur der Wert eines konkreten Attributs im Feld.
    // Siehe https://github.com/primefaces/primeng/issues/2242
    convertNextResponsiblePersonDisplay(displayedPerson: ErpWorkflowPersonData): string {
        let personString = '';

        if (displayedPerson && displayedPerson.surname) {
            personString = `${displayedPerson.surname}, ${displayedPerson.firstname}`;

            if (displayedPerson.nameAddition) {
                personString += ` (${displayedPerson.nameAddition})`;
            }
        }

        return personString;
    }

    // In der Auswahlliste für Bearbeitungsschritte erscheinen auch Schritte, die aufgrund des aktuellen Antragsstatus nicht wählbar sind.
    // Diese Schritte erhalten einen Hinweis, warum sie nicht wählbar sind (z. B. "Vorbedingung nicht erfüllt"). Wenn nun auf solche ein
    // Schritt geklickt wird, darf dieser nicht im Eingabefeld übernommen werden, wenn der Nutzer auf diesen klickt.
    onSelectProcessingStep(processingStep: ErpWorkflowProcessingStepValue) {
        if (this.processingStepDropdown) {
            if (processingStep.isSelectable === false) {
                this.selectedProcessingStep = undefined;
                this.processingStepDropdown.value = undefined;
            }
            this.processingStepDropdown.updateInputField();
        }

        // Filterung der möglichen nächsten zuständigen Bearbeiter auf Basis des gewählten Bearbeitungsschrittes
        this.filterNextResponsiblePersonValues();
    }

    // In der Auswahlliste für die nächste zuständige Person erscheinen auch Personen, die keinen Folgeschritt zum momentan gewählten
    // Bearbeitungsschritt besitzen. Diese Person dürfen nicht auswählbar sein.
    onSelectNextResponsiblePerson(selectedNextResponsiblePerson: ErpWorkflowPersonData) {
        this.fillNextResponsiblePersonInputField( selectedNextResponsiblePerson );
    }

    onCompleteNextResponsiblePersonValues() {
        if (this.nextResponsiblePersonDropdown?.inputValue && this.nextResponsiblePersonDropdown?.inputValue !== '') {
            return;
        }

        this.filterNextResponsiblePersonValues();
    }

    filterNextResponsiblePersonValuesAfterInput(event: KeyboardEvent) {
        this.filterNextResponsiblePersonValues();

        this.filteredNextResponsiblePersonValues = this.filteredNextResponsiblePersonValues.filter(data =>
            ((data.surname || '') + (data.firstname || '')).toLowerCase()
                .indexOf((this.nextResponsiblePersonDropdown?.inputValue?.toLowerCase() || '')) !== -1);
    }

    private fillNextResponsiblePersonInputField( selectedNextResponsiblePerson: ErpWorkflowPersonData ) {
        if ( this.nextResponsiblePersonDropdown ) {
            if ( this.filteredNextResponsiblePersonValues.includes(selectedNextResponsiblePerson) ) {
                this.nextResponsiblePersonDropdown.inputFieldValue =
                    this.convertNextResponsiblePersonDisplay( selectedNextResponsiblePerson );
            } else {
                this.nextResponsiblePersonDropdown.inputFieldValue = '';
                this.selectedNextResponsiblePerson = undefined;
            }
        }
    }

    private filterNextResponsiblePeopleByFulfilledPrecondition( processingStep: ErpWorkflowProcessingStepValue ) {
        this.filteredNextResponsiblePersonValues = [];
        this.filteredNextResponsiblePersonValues.push({});
        this.availableNextResponsiblePersonValues.forEach( person => {
            let hasAtLeastOneFollowingStep = false;

            person.availableProcessingSteps?.forEach( step => {
                if ( !processingStep || !processingStep.isSelectable ||
                     ( processingStep.pseudonymizedStepId &&
                        step.pseudonymizedPreCondition?.includes( processingStep.pseudonymizedStepId ))) {
                    hasAtLeastOneFollowingStep = true;
                }
            } );

            if ( hasAtLeastOneFollowingStep ) {
                this.filteredNextResponsiblePersonValues.push( person );
            }

            if ( this.selectedNextResponsiblePerson === person ) {
                this.fillNextResponsiblePersonInputField(person);
            }
        } );
    }

    private sortProcessingStepAndNextResponsiblePersonLists( workflowOperation: ErpWorkflowOperation ) {
        this.sortProcessingStepList(workflowOperation.erpWorkflowProcessingStepValueDtos);
        this.sortNextResponsiblePersonList(workflowOperation.erpWorkflowPersonDataDtos);
    }

    private sortProcessingStepList(steps: ErpWorkflowProcessingStepValue[]): ErpWorkflowProcessingStepValue[] {
        return steps.sort( ( a, b ) => (( +b.isSelectable ) - ( +a.isSelectable )) ||
            (a.sortorder || 0 ) - (b.sortorder || 0 ) || ( a.text || '' ).localeCompare( b.text || '' ) );
    }

    private sortNextResponsiblePersonList(personData: ErpWorkflowPersonData[]): ErpWorkflowPersonData[] {
        return personData.sort( ( a, b ) =>
            (( a.surname || '' ).localeCompare( b.surname || '' )) || ( a.firstname || '' ).localeCompare( b.firstname || '' ) );
    }

    private saveWorkflowOperation() {
        let workflowOperationStep: ErpWorkflowOperationStep = this.buildWorkflowOperationStep();

        this.erpWorkflowService.createNextWorkflowOperationStep( workflowOperationStep).subscribe({
            next: () => {
                this.hisNotificationService.success(
                    this.hisUiService.translate( 'rm.erp.common.workflow.operationstep.save.success' ),
                    this.hisUiService.translate( 'view.messages.success' ), 5000 );
            } });
    }

    private buildWorkflowOperationStep(): ErpWorkflowOperationStep {
        let pseudonymizedSelectedStepId = '';

        if (this.selectedProcessingStep) {
            pseudonymizedSelectedStepId = this.selectedProcessingStep.pseudonymizedStepId;
        }

        let workflowOperationStep: ErpWorkflowOperationStep = {
            useCaseIdent: this.useCaseIdent,
            processType: this.processType,
            processId: this.processId,
            pseudonymizedStepId: pseudonymizedSelectedStepId,
            pseudonymizedNextResponsiblePersonId: this.selectedNextResponsiblePerson?.pseudonymizedPersonId,
            comment: this.stepExecutionComment
        };

        return workflowOperationStep;
    }

    private preselectNextResponsiblePerson(personData: ErpWorkflowPersonData[]) {
        personData.forEach( person => {
            if (person.pseudonymizedPersonId === this.pseudonymizedPreallocatedPersonId) {
                this.selectedNextResponsiblePerson = person;
            }
        });
    }
}
